import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ChevronLeft, ChevronRight, Send } from 'lucide-react';
import './App.css';

const API_BASE = '/api';

const Form = () => {
  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [responses, setResponses] = useState({});
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formError, setFormError] = useState('');
  const [isNextDisabled, setIsNextDisabled] = useState(false);
  const [firstNameError, setFirstNameError] = useState('');
  const [lastNameError, setLastNameError] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [openSingleSelectId, setOpenSingleSelectId] = useState(null);
  const [showCountryCodeDropdown, setShowCountryCodeDropdown] = useState(false);
  const [selectedCountryCode, setSelectedCountryCode] = useState({ code: '+91', label: 'India (+91)' });
  const [phoneDigits, setPhoneDigits] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const initForm = async () => {
      await loadQuestions();
      await startForm();
    };
    initForm();
  }, []);

  useEffect(() => {
    setFormError('');
    setIsNextDisabled(false);
    setFirstNameError('');
    setLastNameError('');
    setOpenDropdownId(null);
    setOpenSingleSelectId(null);
    setShowCountryCodeDropdown(false);
  }, [currentQuestion]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdownId && !event.target.closest('.multiselect-dropdown')) {
        setOpenDropdownId(null);
      }
      if (openSingleSelectId && !event.target.closest('.singleselect-dropdown')) {
        setOpenSingleSelectId(null);
      }
      if (showCountryCodeDropdown && !event.target.closest('.country-code-dropdown')) {
        setShowCountryCodeDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openDropdownId, openSingleSelectId, showCountryCodeDropdown]);

  const loadQuestions = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE}/questions`);
      setQuestions(response.data);
      setLoading(false);
    } catch (err) {
      setError('Failed to load questions');
      setLoading(false);
    }
  };

  const startForm = async () => {
    try {
      const sessionResponse = await axios.post(`${API_BASE}/session`);
      setSessionId(sessionResponse.data.sessionId);
    } catch (err) {
      setError('Failed to start form');
    }
  };

  const saveResponse = async (questionId, answer) => {
    if (!sessionId) return;

    try {
      await axios.post(`${API_BASE}/response`, {
        sessionId,
        questionId,
        answer
      });
    } catch (err) {
      console.error('Error saving response:', err);
    }
  };

  const handleAnswerChange = (value) => {
    const question = questions[currentQuestion];
    const newResponses = { ...responses, [question.id]: value };
    setResponses(newResponses);

    const errorMessage = validateField(question, value);
    setFormError(errorMessage);
    setIsNextDisabled(errorMessage !== '');

    setTimeout(() => {
      saveResponse(question.id, value);
    }, 1000);
  };

  const isCurrentQuestionAnswered = () => {
    const question = questions[currentQuestion];
    const answer = responses[question.id];

    if (!question.is_mandatory) return true;

    if (question.field_type === 'compound_name') {
      const firstName = responses[`${question.id}_first`];
      const lastName = responses[`${question.id}_last`];
      return firstName && firstName.trim() !== '' && lastName && lastName.trim() !== '' && !firstNameError && !lastNameError;
    }

    if (question.field_type === 'checkbox' || question.field_type === 'multiselect') {
      return answer && Array.isArray(answer) && answer.length > 0;
    }

    return answer && answer.toString().trim() !== '';
  };

  const handleNextClick = () => {
    if (!isCurrentQuestionAnswered() || formError !== '') {
      setFormError('This field is required to continue.');
      return;
    }

    setFormError('');

    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const goToPrev = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const validateAllMandatoryFields = () => {
    const missingFields = [];

    questions.forEach(question => {
      if (!question.is_mandatory) return;

      const answer = responses[question.id];

      if (question.field_type === 'compound_name') {
        const firstName = responses[`${question.id}_first`];
        const lastName = responses[`${question.id}_last`];
        const firstError = validateIndividualName(firstName || '');
        const lastError = validateIndividualName(lastName || '');
        if (!firstName || firstName.trim() === '' || !lastName || lastName.trim() === '' || firstError || lastError) {
          missingFields.push(question.question_text);
        }
      } else {
        if (question.field_type === 'checkbox' || question.field_type === 'multiselect') {
          if (!answer || !Array.isArray(answer) || answer.length === 0) {
            missingFields.push(question.question_text);
          }
        } else {
          if (!answer || answer.toString().trim() === '') {
            missingFields.push(question.question_text);
          }
        }
      }
    });

    return missingFields;
  };

  const submitForm = async () => {
    const missingFields = validateAllMandatoryFields();

    if (missingFields.length > 0) {
      alert(`Please complete all mandatory fields:\n\n${missingFields.join('\n')}`);
      return;
    }

    try {
      await axios.post(`${API_BASE}/submit`, { sessionId });
      alert('Form submitted successfully! Thank you for your responses.');
      navigate('/');
    } catch (err) {
      if (err.response?.data?.missingQuestions) {
        alert(`Please complete all mandatory fields:\n\n${err.response.data.missingQuestions.join('\n')}`);
      } else {
        alert('Error submitting form: ' + (err.response?.data?.message || err.message));
      }
    }
  };

  // Validation functions
  const validateName = (name) => {
    const regex = /^[a-zA-Z\s]+$/;
    if (!regex.test(name)) {
      return 'Name must only contain alphabetic characters and spaces.';
    }
    if (name.length > 255) {
      return 'Name must not exceed 255 characters.';
    }
    return '';
  };

  const validateIndividualName = (name) => {
    if (!name.trim()) return '';
    const regex = /^[a-zA-Z\s]+$/;
    if (!regex.test(name)) {
      return 'Name must only contain alphabetic characters and spaces.';
    }
    if (name.length > 255) {
      return 'Name must not exceed 255 characters.';
    }
    return '';
  };

  const validateEmail = (email) => {
    const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return regex.test(email) ? '' : 'Invalid email format.';
  };

  const validatePhoneNumber = (phone) => {
    return '';
  };

  const validateNumber = (value, question) => {
    const num = parseFloat(value);
    if (isNaN(num)) {
      return 'Please enter a valid number.';
    }
    if (question.validation_rules?.min !== undefined && num < question.validation_rules.min) {
      return `Value must be at least ${question.validation_rules.min}.`;
    }
    if (question.validation_rules?.max !== undefined && num > question.validation_rules.max) {
      return `Value must be at most ${question.validation_rules.max}.`;
    }
    return '';
  };

  const validateField = (question, value) => {
    switch (question.field_type) {
      case 'text':
        if (question.question_text === 'Mobile no') {
          return validatePhoneNumber(value);
        }
        if (question.question_text === 'Height in cm' || question.question_text === 'Weight in kg') {
          if (!/^\d{0,3}(\.\d*)?$/.test(value)) {
            return 'Enter a valid number (e.g., 170.5).';
          }
          return '';
        }
        return '';
      case 'email':
        return validateEmail(value);
      case 'number':
        return validateNumber(value, question);
      default:
        return '';
    }
  };

  // Render preview of all responses
  const renderPreview = () => {
    return (
      <div className="preview-content">
        <h3>Review Your Responses</h3>
        <div className="preview-questions">
          {questions.map((question, index) => {
            let answer = responses[question.id] || '';

            if (question.field_type === 'compound_name') {
              const firstName = responses[`${question.id}_first`] || '';
              const lastName = responses[`${question.id}_last`] || '';
              answer = `${firstName} ${lastName}`.trim();
            }

            if (question.field_type === 'file' && answer) {
              try {
                const parsedData = JSON.parse(answer);
                if (Array.isArray(parsedData)) {
                  answer = (
                    <div className="multiple-files-preview">
                      <div className="files-count">{parsedData.length} file(s) uploaded</div>
                      {parsedData.map((fileData, fileIndex) => (
                        <div key={fileIndex} className="file-preview">
                          <div className="file-info">
                            <strong>File {fileIndex + 1}:</strong> {fileData.originalName}
                            <br />
                            <small>Size: {(fileData.size / 1024 / 1024).toFixed(2)} MB</small>
                          </div>
                          <div className="file-actions">
                            <button
                              onClick={() => window.open(`${API_BASE.replace('/api', '')}${fileData.url}`, '_blank')}
                              className="file-view-btn"
                            >
                              View
                            </button>
                            <button
                              onClick={() => {
                                const link = document.createElement('a');
                                link.href = `${API_BASE.replace('/api', '')}/api/files/${fileData.filename}/download`;
                                link.download = fileData.originalName;
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                              }}
                              className="file-download-btn"
                            >
                              Download
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                } else {
                  answer = (
                    <div className="file-preview">
                      <div className="file-info">
                        <strong>File:</strong> {parsedData.originalName}
                        <br />
                        <small>Size: {(parsedData.size / 1024 / 1024).toFixed(2)} MB</small>
                      </div>
                    </div>
                  );
                }
              } catch (e) {
                // keep as-is
              }
            }

            if (question.field_type === 'checkbox' || question.field_type === 'multiselect') {
              if (Array.isArray(answer)) {
                answer = answer.join(', ');
              } else {
                try {
                  const parsed = JSON.parse(answer);
                  if (Array.isArray(parsed)) {
                    answer = parsed.join(', ');
                  }
                } catch (e) {
                  // Keep original
                }
              }
            }

            return (
              <div key={question.id} className="preview-item">
                <div className="preview-question-number">Q{index + 1}</div>
                <div className="preview-question-content">
                  <strong>{question.question_text}</strong>
                  <div className="preview-answer">
                    {answer || <em>No answer provided</em>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Render form field based on type
  const renderFormField = (question) => {
    const value = responses[question.id] || '';

    switch (question.field_type) {
      case 'compound_name':
        const firstName = responses[`${question.id}_first`] || '';
        const lastName = responses[`${question.id}_last`] || '';
        return (
          <div className="name-fields">
            <div className="name-field">
              <input
                type="text"
                value={firstName}
                onChange={(e) => {
                  const value = e.target.value;
                  const newResponses = { ...responses, [`${question.id}_first`]: value };
                  setResponses(newResponses);
                  const error = validateIndividualName(value);
                  setFirstNameError(error);
                  setTimeout(() => saveResponse(`${question.id}_first`, value), 1000);
                }}
                placeholder="First Name"
                className="form-input"
              />
              <label className="field-label">First Name*</label>
              {firstNameError && <div className="error-alert">{firstNameError}</div>}
            </div>
            <div className="name-field">
              <input
                type="text"
                value={lastName}
                onChange={(e) => {
                  const value = e.target.value;
                  const newResponses = { ...responses, [`${question.id}_last`]: value };
                  setResponses(newResponses);
                  const error = validateIndividualName(value);
                  setLastNameError(error);
                  setTimeout(() => saveResponse(`${question.id}_last`, value), 1000);
                }}
                placeholder="Last Name"
                className="form-input"
              />
              <label className="field-label">Last Name*</label>
              {lastNameError && <div className="error-alert">{lastNameError}</div>}
            </div>
          </div>
        );

      case 'textarea':
        return (
          <textarea
            value={value}
            onChange={(e) => handleAnswerChange(e.target.value)}
            placeholder="Enter your response..."
            rows={4}
            className="form-textarea"
          />
        );

      case 'text':
      case 'email':
        if (question.question_text === 'Mobile no') {
          const countryCodes = [
            { code: '+91', label: 'India (+91)' },
            { code: '+1', label: 'USA/Canada (+1)' },
            { code: '+44', label: 'UK (+44)' },
            { code: '+61', label: 'Australia (+61)' },
            { code: '+971', label: 'UAE (+971)' },
            { code: '+65', label: 'Singapore (+65)' },
            { code: '+60', label: 'Malaysia (+60)' },
            { code: '+49', label: 'Germany (+49)' },
            { code: '+33', label: 'France (+33)' },
            { code: '+81', label: 'Japan (+81)' },
            { code: '+86', label: 'China (+86)' },
            { code: '+82', label: 'South Korea (+82)' },
            { code: '+966', label: 'Saudi Arabia (+966)' },
            { code: '+974', label: 'Qatar (+974)' },
            { code: '+968', label: 'Oman (+968)' },
            { code: '+94', label: 'Sri Lanka (+94)' },
            { code: '+977', label: 'Nepal (+977)' },
            { code: '+880', label: 'Bangladesh (+880)' },
            { code: '+27', label: 'South Africa (+27)' },
            { code: '+55', label: 'Brazil (+55)' },
          ];
          return (
            <div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'stretch' }}>
                <div className="country-code-dropdown" style={{ position: 'relative', width: '180px', flexShrink: 0 }}>
                  <button
                    type="button"
                    className="singleselect-trigger"
                    style={{ height: '100%', fontSize: '0.95rem' }}
                    onClick={() => setShowCountryCodeDropdown(!showCountryCodeDropdown)}
                  >
                    <span>{selectedCountryCode.label}</span>
                    <span className="dropdown-arrow">&#9660;</span>
                  </button>
                  {showCountryCodeDropdown && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      background: 'white',
                      border: '2px solid #e2e8f0',
                      borderTop: 'none',
                      borderRadius: '0 0 8px 8px',
                      maxHeight: '250px',
                      overflowY: 'auto',
                      zIndex: 99999,
                      boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                    }}>
                      {countryCodes.map((c, i) => (
                        <div
                          key={i}
                          className="singleselect-option"
                          onClick={() => {
                            setSelectedCountryCode(c);
                            handleAnswerChange(c.code + phoneDigits);
                            setShowCountryCodeDropdown(false);
                          }}
                        >
                          <span>{c.label}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <input
                  type="text"
                  value={phoneDigits}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === '' || /^\d*$/.test(val)) {
                      setPhoneDigits(val);
                      handleAnswerChange(selectedCountryCode.code + val);
                    }
                  }}
                  placeholder="Enter phone number"
                  className="form-input"
                  style={{ flex: 1 }}
                />
              </div>
            </div>
          );
        }
        if (question.question_text === 'Height in cm' || question.question_text === 'Weight in kg') {
          return (
            <input
              type="text"
              value={value}
              onChange={(e) => {
                const val = e.target.value;
                if (val === '' || /^\d{0,3}(\.\d*)?$/.test(val)) {
                  handleAnswerChange(val);
                }
              }}
              placeholder={question.question_text === 'Height in cm' ? 'e.g., 170.5' : 'e.g., 65.5'}
              className="form-input"
            />
          );
        }
        return (
          <input
            type={question.field_type}
            value={value}
            onChange={(e) => handleAnswerChange(e.target.value)}
            placeholder={`Enter your ${question.field_type}`}
            className="form-input"
          />
        );

      case 'number':
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => handleAnswerChange(e.target.value)}
            placeholder="Enter a number"
            className="form-input"
            min={question.validation_rules?.min}
            max={question.validation_rules?.max}
            step={question.validation_rules?.step}
          />
        );

      case 'radio':
        return (
          <div className="radio-group">
            {question.options.map((option, index) => (
              <label key={index} className="radio-label">
                <input
                  type="radio"
                  name={`question-${question.id}`}
                  value={option}
                  checked={value === option}
                  onChange={(e) => handleAnswerChange(e.target.value)}
                />
                <span className="radio-text">{option}</span>
              </label>
            ))}
          </div>
        );

      case 'checkbox':
        const selectedValues = Array.isArray(value) ? value : [];
        return (
          <div className="checkbox-group">
            {question.options.map((option, index) => (
              <label key={index} className="checkbox-label">
                <input
                  type="checkbox"
                  value={option}
                  checked={selectedValues.includes(option)}
                  onChange={(e) => {
                    const newValues = e.target.checked
                      ? [...selectedValues, option]
                      : selectedValues.filter(v => v !== option);
                    handleAnswerChange(newValues);
                  }}
                />
                <span className="checkbox-text">{option}</span>
              </label>
            ))}
          </div>
        );

      case 'select':
        // Questions that need custom single-select dropdown
        const customSingleSelectQuestions = [4, 10, 20, 21, 22, 23, 24];
        const useCustomSingleSelect = customSingleSelectQuestions.includes(question.question_number);

        if (useCustomSingleSelect) {
          return (
            <div className="singleselect-container">
              <div className="singleselect-dropdown">
                <button
                  type="button"
                  className="singleselect-trigger"
                  onClick={() => {
                    setOpenSingleSelectId(openSingleSelectId === question.id ? null : question.id);
                  }}
                >
                  <span>
                    {value || '\u00A0'}
                  </span>
                  <span className="dropdown-arrow">&#9660;</span>
                </button>

                <div
                  id={`singleselect-dropdown-${question.id}`}
                  className="singleselect-options"
                  style={{display: openSingleSelectId === question.id ? 'block' : 'none'}}
                >
                  {question.options.map((option, index) => (
                    <div
                      key={index}
                      className={`singleselect-option ${value === option ? 'selected' : ''}`}
                      onClick={() => {
                        handleAnswerChange(option);
                        setOpenSingleSelectId(null);
                      }}
                    >
                      <span>{option}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        }

        return (
          <select
            value={value}
            onChange={(e) => handleAnswerChange(e.target.value)}
            className="form-select"
          >
            <option value=""></option>
            {question.options.map((option, index) => (
              <option key={index} value={option}>{option}</option>
            ))}
          </select>
        );

      case 'multiselect':
        const multiSelectedValues = Array.isArray(value) ? value : [];

        return (
          <div className="multiselect-container">
            {multiSelectedValues.length > 0 && (
              <div className="selected-items">
                {multiSelectedValues.map((item, index) => (
                  <div key={index} className="selected-item">
                    <span>{item}</span>
                    <button
                      type="button"
                      onClick={() => {
                        const newValues = multiSelectedValues.filter(v => v !== item);
                        handleAnswerChange(newValues);
                      }}
                      className="remove-item"
                    >
                      x
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="multiselect-dropdown">
              <button
                type="button"
                className="multiselect-trigger"
                onClick={() => {
                  setOpenDropdownId(openDropdownId === question.id ? null : question.id);
                }}
              >
                <span>
                  {multiSelectedValues.length === 0
                    ? 'Select options...'
                    : `${multiSelectedValues.length} selected`
                  }
                </span>
                <span className="dropdown-arrow">&#9660;</span>
              </button>

              <div
                id={`dropdown-${question.id}`}
                className="multiselect-options"
                style={{display: openDropdownId === question.id ? 'block' : 'none'}}
              >
                {question.options.map((option, index) => (
                  <label key={index} className="multiselect-option">
                    <input
                      type="checkbox"
                      checked={multiSelectedValues.includes(option)}
                      onChange={(e) => {
                        const newValues = e.target.checked
                          ? [...multiSelectedValues, option]
                          : multiSelectedValues.filter(v => v !== option);
                        handleAnswerChange(newValues);
                      }}
                    />
                    <span>{option}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        );

      case 'file':
        return (
          <div className="file-upload-container">
            <input
              type="file"
              multiple
              onChange={async (e) => {
                const files = Array.from(e.target.files);
                if (files.length > 0) {
                  try {
                    const formData = new FormData();
                    files.forEach((file) => {
                      formData.append('files', file);
                    });

                    const uploadResponse = await axios.post(`${API_BASE}/upload`, formData, {
                      headers: {
                        'Content-Type': 'multipart/form-data',
                      },
                    });

                    if (uploadResponse.data.files) {
                      const filesData = uploadResponse.data.files.map(file => ({
                        filename: file.filename,
                        originalName: file.originalName,
                        url: file.url,
                        size: file.size,
                        type: file.mimetype
                      }));
                      handleAnswerChange(JSON.stringify(filesData));
                    } else if (uploadResponse.data.file) {
                      const fileData = {
                        filename: uploadResponse.data.file.filename,
                        originalName: uploadResponse.data.file.originalName,
                        url: uploadResponse.data.file.url,
                        size: uploadResponse.data.file.size,
                        type: uploadResponse.data.file.mimetype
                      };
                      handleAnswerChange(JSON.stringify([fileData]));
                    }
                  } catch (error) {
                    console.error('File upload failed:', error);
                    alert('File upload failed. Please try again.');
                  }
                }
              }}
              className="form-file"
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.csv,.xlsx,.xls"
            />
            <div className="file-help">
              Supported formats: PDF, Images, Documents (Max 10MB per file, up to 10 files)
            </div>
            {responses[question.id] && (() => {
              try {
                const filesData = JSON.parse(responses[question.id]);
                if (Array.isArray(filesData) && filesData.length > 0) {
                  return (
                    <div className="uploaded-files-list">
                      <h4>Uploaded Files ({filesData.length}):</h4>
                      {filesData.map((file, index) => (
                        <div key={index} className="uploaded-file-item">
                          <span className="file-name">{file.originalName}</span>
                          <span className="file-size">({(file.size / 1024 / 1024).toFixed(2)} MB)</span>
                        </div>
                      ))}
                    </div>
                  );
                }
              } catch (e) {
                // Ignore parsing errors
              }
              return null;
            })()}
          </div>
        );

      default:
        return <div>Unsupported field type: {question.field_type}</div>;
    }
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  if (questions.length === 0) {
    return (
      <div className="no-questions">
        <h2>No questions available</h2>
        <p>Please contact the administrator.</p>
      </div>
    );
  }

  const currentQ = questions[currentQuestion];
  const isLastQuestion = currentQuestion === questions.length - 1;

  return (
    <div className="form-container">
      <div className="form-header">
        <div className="header-content-center">
          <h2 className="header-subtitle">Nutrition Questionnaire</h2>
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{ width: `${((currentQuestion + 1) / questions.length) * 100}%` }}
            ></div>
          </div>
          <p className="question-counter">
            Question {currentQuestion + 1} of {questions.length}
          </p>
        </div>
      </div>

      <div className="question-container">
        <h2 className="question-title">
          {currentQuestion + 1}. {currentQ.question_text}
          {currentQ.is_mandatory && <span className="required">*</span>}
        </h2>

        <div className="form-field-container">
          {renderFormField(currentQ)}
        </div>
      </div>

      {formError && <div className="error-alert-fixed">{formError}</div>}

      <div className="form-navigation">
        <button
          onClick={goToPrev}
          disabled={currentQuestion === 0}
          className="nav-btn prev-btn"
        >
          <ChevronLeft size={20} />
          Prev
        </button>

        <div></div>

        {isLastQuestion ? (
          <button
            onClick={() => {
              if (!isCurrentQuestionAnswered()) {
                setFormError('This field is required to continue.');
                setTimeout(() => setFormError(''), 3000);
                return;
              }
              setShowPreview(true);
            }}
            className="nav-btn submit-btn"
          >
            <Send size={20} />
            Submit
          </button>
        ) : (
          <button
            onClick={handleNextClick}
            className="nav-btn next-btn"
            disabled={isNextDisabled}
          >
            Next
            <ChevronRight size={20} />
          </button>
        )}
      </div>

      {showPreview && (
        <div className="modal-overlay" onClick={() => setShowPreview(false)}>
          <div className="modal-content preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Form Preview</h2>
              <button
                onClick={() => setShowPreview(false)}
                className="close-btn"
              >
                x
              </button>
            </div>
            <div className="modal-body">
              {renderPreview()}
            </div>
            <div className="modal-footer">
              <button
                onClick={() => setShowPreview(false)}
                className="nav-btn cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowPreview(false);
                  submitForm();
                }}
                className="nav-btn submit-btn"
              >
                <Send size={20} />
                Submit Form
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Form;
